import React from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { format, parseISO, isValid } from 'date-fns';
import { motion } from 'framer-motion';
import styles from './CandidateProfile.module.css';

function displayRating(value) {
  if (value == null) return '—';
  const num = Number(value);
  if (Number.isNaN(num)) return '—';
  return (num > 10 ? num / 10 : num).toFixed(1);
}

export default function CandidateProfile() {
  const { id } = useParams();
  const location = useLocation();
  const profile = location.state?.profile;

  if (!profile) {
    return (
      <motion.div
        className={styles.page}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Link to="/recruiter/search" className={styles.back}>
          ← Back to search
        </Link>
        <article className={styles.card}>
          <h1 className={styles.name}>Candidate profile</h1>
          <p className={styles.meta}>Profile ID: {id}</p>
          <p style={{ color: 'rgba(203, 213, 225, 0.8)', lineHeight: 1.6 }}>
            Open this profile from search results to view full details without an extra API call.
          </p>
        </article>
      </motion.div>
    );
  }

  const {
    candidateName,
    headline,
    currentRole,
    skills = [],
    yearsExperience,
    location: profileLocation,
    samastixRating,
    interviewCount,
    availableFrom,
    expectedSalary,
  } = profile;

  const availableLabel = (() => {
    if (!availableFrom) return 'Available now';
    const d = parseISO(String(availableFrom));
    return isValid(d) ? `Available ${format(d, 'MMM yyyy')}` : 'Available now';
  })();

  return (
    <motion.div
      className={styles.page}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <Link to="/recruiter/search" className={styles.back}>
        ← Back to search
      </Link>

      <article className={styles.card}>
        <h1 className={styles.name}>{candidateName}</h1>
        <p className={styles.meta}>
          {currentRole || headline || 'Candidate'}
          {yearsExperience != null ? ` · ${yearsExperience} yrs exp` : ''}
          {profileLocation ? ` · ${profileLocation}` : ''}
        </p>

        <div className={styles.stats}>
          <span>★ {displayRating(samastixRating)} Samastix rating</span>
          <span>{interviewCount} interview{interviewCount === 1 ? '' : 's'}</span>
          <span>{availableLabel}</span>
          {expectedSalary != null && <span>Expected: ₹{expectedSalary}L</span>}
        </div>

        {skills.length > 0 && (
          <>
            <h2 className={styles.sectionTitle}>Skills</h2>
            <motion.div
              className={styles.skills}
              initial="hidden"
              animate="visible"
              variants={{ visible: { transition: { staggerChildren: 0.06 } } }}
            >
              {skills.map((skill) => (
                <motion.span
                  key={skill}
                  className={styles.skill}
                  variants={{
                    hidden: { opacity: 0, y: 8 },
                    visible: { opacity: 1, y: 0 },
                  }}
                >
                  {skill}
                </motion.span>
              ))}
            </motion.div>
          </>
        )}
      </article>
    </motion.div>
  );
}
